import React, { useState, useRef, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Play, Save, Plus, MessageSquare, GitBranch, Tag, Clock, Send, Filter, Users, Settings as SettingsIcon, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockFlows } from '../data/mockData';
import { useToast } from '../hooks/use-toast';

const FlowBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const canvasRef = useRef(null);
  
  const currentFlow = id ? mockFlows.find(f => f.id === id) : null;
  
  const [flowName, setFlowName] = useState(currentFlow?.name || 'New Flow');
  const [nodes, setNodes] = useState(currentFlow?.nodes || []);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedNode, setDraggedNode] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });

  const blockTypes = [
    { type: 'trigger', label: 'Trigger', icon: Play, color: 'bg-green-100 text-green-600' },
    { type: 'message', label: 'Send Message', icon: MessageSquare, color: 'bg-blue-100 text-blue-600' },
    { type: 'condition', label: 'Condition', icon: GitBranch, color: 'bg-purple-100 text-purple-600' },
    { type: 'action', label: 'Action', icon: Send, color: 'bg-orange-100 text-orange-600' },
    { type: 'tag', label: 'Add Tag', icon: Tag, color: 'bg-pink-100 text-pink-600' },
    { type: 'delay', label: 'Delay', icon: Clock, color: 'bg-yellow-100 text-yellow-600' },
    { type: 'filter', label: 'Filter', icon: Filter, color: 'bg-indigo-100 text-indigo-600' },
    { type: 'segment', label: 'Segment', icon: Users, color: 'bg-teal-100 text-teal-600' }
  ];

  const handleDragStart = (e, blockType) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('blockType', JSON.stringify(blockType));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const blockType = JSON.parse(e.dataTransfer.getData('blockType'));
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - panPosition.x) / (zoom / 100);
    const y = (e.clientY - rect.top - panPosition.y) / (zoom / 100);
    
    const newNode = {
      id: `node-${Date.now()}`,
      type: blockType.type,
      position: { x, y },
      data: {
        label: blockType.label,
        content: blockType.type === 'message' ? 'Your message here...' : ''
      }
    };
    
    setNodes([...nodes, newNode]);
    toast({
      title: 'Block added',
      description: `${blockType.label} added to flow`
    });
  }, [nodes, zoom, panPosition, toast]);

  const handleNodeClick = (node) => {
    setSelectedNode(node);
  };

  const handleSave = () => {
    toast({
      title: 'Flow saved',
      description: 'Your automation flow has been saved successfully'
    });
  };

  const handleTest = () => {
    toast({
      title: 'Testing flow',
      description: 'Flow test started. Check your connected platform.'
    });
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col h-full">
        {/* Top Bar */}
        <div className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/flows')}
              >
                ← Back
              </Button>
              <Input
                value={flowName}
                onChange={(e) => setFlowName(e.target.value)}
                className="max-w-xs font-semibold"
              />
              <span className="text-sm text-gray-500">Last edited: 5 min ago</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2 mr-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setZoom(Math.max(50, zoom - 10))}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-600 w-12 text-center">{zoom}%</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setZoom(Math.min(200, zoom + 10))}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon">
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </div>
              <Button variant="outline" onClick={handleTest}>
                <Play className="h-4 w-4 mr-2" />
                Test
              </Button>
              <Button onClick={handleSave}>
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
            </div>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar - Block Library */}
          <div className="w-64 bg-white border-r p-4 overflow-y-auto">
            <h3 className="font-semibold text-gray-900 mb-4">Building Blocks</h3>
            <div className="space-y-2">
              {blockTypes.map((block) => {
                const Icon = block.icon;
                return (
                  <div
                    key={block.type}
                    draggable
                    onDragStart={(e) => handleDragStart(e, block)}
                    className={`${block.color} p-3 rounded-lg cursor-move hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-center space-x-2">
                      <Icon className="h-5 w-5" />
                      <span className="font-medium text-sm">{block.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-6">
              <h4 className="font-semibold text-gray-900 mb-3 text-sm">Quick Tips</h4>
              <ul className="text-xs text-gray-600 space-y-2">
                <li>• Drag blocks onto canvas</li>
                <li>• Click to configure</li>
                <li>• Connect blocks to create flow</li>
                <li>• Test before activating</li>
              </ul>
            </div>
          </div>

          {/* Canvas Area */}
          <div className="flex-1 bg-gray-50 overflow-hidden relative">
            <div
              ref={canvasRef}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle, #e5e7eb 1px, transparent 1px)',
                backgroundSize: '20px 20px',
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top left'
              }}
            >
              {nodes.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Plus className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg font-medium">Drag blocks here to start building</p>
                    <p className="text-gray-400 text-sm mt-2">Create your automation flow visually</p>
                  </div>
                </div>
              ) : (
                <svg className="absolute inset-0 w-full h-full pointer-events-none">
                  {/* Connection lines will be drawn here */}
                </svg>
              )}
              
              {/* Render Nodes */}
              {nodes.map((node) => {
                const blockType = blockTypes.find(b => b.type === node.type);
                const Icon = blockType?.icon || MessageSquare;
                const isSelected = selectedNode?.id === node.id;
                
                return (
                  <Card
                    key={node.id}
                    className={`absolute cursor-pointer transition-all hover:shadow-lg ${
                      isSelected ? 'ring-2 ring-blue-500' : ''
                    }`}
                    style={{
                      left: `${node.position.x}px`,
                      top: `${node.position.y}px`,
                      width: '200px'
                    }}
                    onClick={() => handleNodeClick(node)}
                  >
                    <div className={`p-3 ${blockType?.color || 'bg-gray-100'} rounded-t-lg`}>
                      <div className="flex items-center space-x-2">
                        <Icon className="h-4 w-4" />
                        <span className="font-medium text-sm">{node.data.label}</span>
                      </div>
                    </div>
                    {node.data.content && (
                      <div className="p-3 text-sm text-gray-600">
                        {node.data.content}
                      </div>
                    )}
                    {/* Connection points */}
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white border-2 border-blue-500 rounded-full" />
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white border-2 border-blue-500 rounded-full" />
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Right Sidebar - Node Properties */}
          {selectedNode && (
            <div className="w-80 bg-white border-l p-4 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Block Settings</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedNode(null)}
                >
                  ✕
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Block Name
                  </label>
                  <Input value={selectedNode.data.label} />
                </div>
                
                {selectedNode.type === 'message' && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Message Content
                    </label>
                    <textarea
                      className="w-full border rounded-md p-2 text-sm"
                      rows={4}
                      value={selectedNode.data.content}
                      placeholder="Enter your message..."
                    />
                  </div>
                )}
                
                {selectedNode.type === 'delay' && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Delay Duration
                    </label>
                    <div className="flex items-center space-x-2">
                      <Input type="number" placeholder="5" className="w-20" />
                      <select className="border rounded-md p-2 text-sm">
                        <option>Minutes</option>
                        <option>Hours</option>
                        <option>Days</option>
                      </select>
                    </div>
                  </div>
                )}
                
                <div>
                  <Button variant="destructive" className="w-full">
                    Delete Block
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FlowBuilder;
